import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";

import Modal from "pages/members/components/Modal";
import { deleteMember, fetchMembers, updateMemberAuth } from "shared/api/MemberApi";
import { decrypt } from "shared/lib/crypto/decrypt";
import { formatToKST } from "shared/lib/date/formatKst";
import { alertDialog, confirmDialog } from "shared/ui/feedback";
import { AddHeader, BaseContainer } from "shared/ui/layout";
import {
  DataTable,
  Pagination,
  useClientPagination,
} from "shared/ui/table";

function AdminPermissions() {
  const normalizeMember = useCallback((member) => ({
    ...member,
    createdAtDisplay: formatToKST(member?.createdAt),
    lastLoginDisplay: formatToKST(member?.lastLogin),
  }), []);
  const navigate = useNavigate();
  const [memberData, setMemberData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 10;
  const { pageItems, page, setPage, pageSize, totalCount } =
    useClientPagination(memberData, { pageSize: itemsPerPage });
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [category, setCategory] = useState(0);
  const [originalCategory, setOriginalCategory] = useState(0);

  const readMembers = useCallback(async () => {
    try {
      const members = await fetchMembers();
      const normalizedData = (members || [])
        .map(normalizeMember)
        .slice()
        .reverse();
      setMemberData(normalizedData);
    } catch (error) {
    }
  }, [normalizeMember]);

  useEffect(() => {
    readMembers();
  }, [readMembers]);

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      { Header: "이름", accessor: "name" },
      {
        Header: "이메일",
        accessor: "email",
        Cell: ({ row }) => {
          const decryptedEmail = decrypt(row.values.email); // 이메일 복호화
          return decryptedEmail;
        },
      },
      { Header: "가입일", accessor: "createdAtDisplay" },
      { Header: "최근 로그인", accessor: "lastLoginDisplay" },
      {
        Header: "구분",
        accessor: "category",
        Cell: ({ row }) => {
          if (row.original.auth === 4) {
            return "마스터";
          } else if (row.original.auth === 3) {
            return "총학";
          } else if (row.original.auth === 2) {
            return `관리자`;
          } else if (row.original.auth === 1) {
            return "일반";
          } else {
            return "비회원";
          }
        },
      },

      // { Header: "조회수", accessor: "view" },

      {
        Header: "관리",
        accessor: "manage",
        Cell: ({ row }) => (
          <ManageBtn onClick={() => handleManageClick(row)}>관리</ManageBtn>
        ),
      },
    ],
    [],
  );

  function handleManageClick(row) {
    // '설정' 버튼 클릭 시 동작 구현
    setOriginalCategory(row.original.auth);
    setCategory(row.original.auth);
    setId(row.original.id);
    setName(row.original.name);
    setEmail(row.original.email);
    setCreatedAt(row.original.createdAtDisplay);
    setUpdatedAt(row.original.lastLoginDisplay);
    setIsModalOpen(true);
  }

  const onWithdraw = async () => {
    try {
      await deleteMember(id);
      // 전체 페이지 리로드 대신 목록만 다시 불러와 갱신한다.
      await readMembers();
    } catch (error) {
      alertDialog("탈퇴 처리에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
    setIsModalOpen(false);
  };

  const onCancel = () => {
    setIsModalOpen(false);
  };

  const onConfirm = async () => {
    try {
      await updateMemberAuth(id, category);
      // 전체 페이지 리로드 대신 목록만 다시 불러와 갱신한다.
      await readMembers();
    } catch (error) {
      alertDialog("회원 정보 수정에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
    setIsModalOpen(false);
  };

  const onClickCategory = async (index) => {
    if (index === 0 && originalCategory === 1) {
      setCategory(1);
    } else if (index === 0) {
      const shouldProceed = await confirmDialog(
        "해당 회원의 관리자 권한을 제거하시겠습니까?\n일반 회원으로 전환 시 관리자 권한이 모두 박탈됩니다.",
      );

      if (shouldProceed) {
        setCategory(1);
      }
    } else if (index === 1) {
      setCategory(2);
    } else if (index === 2) {
      setCategory(4);
    }
  };

  const onClickAdminRegister = () => {
    navigate("./adminRegister");
  };

  return (
    <BaseContainer>
      <AddHeader
        pageName="회원 관리"
        onClick={onClickAdminRegister}
        buttonValue="관리자 추가하기"
      />
      <div style={{ margin: "20px 0" }}>
        총{" "}
        <span style={{ color: "var(--admin-blue-default-blue, #0079E0)" }}>
          {totalCount}
        </span>
        명
      </div>
      <DataTable columns={columns} data={pageItems} />
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
      <Modal // 모달 창 오픈
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        adminPage={true}
        // 회원 정보 관련
        name={name}
        email={decrypt(email)}
        createdAt={createdAt}
        updatedAt={updatedAt}
        category={category}
        onClickCategory={onClickCategory}
        // 탈퇴 컨펌 모달 관련
        onWithdraw={onWithdraw}
        // 취소 컨펌 모달 관련
        onCancel={onCancel}
        // 수정 컨펌 모달 관련
        onConfirm={onConfirm}
        confirmDisabled={category === originalCategory}
        dirty={category !== originalCategory}
      />
    </BaseContainer>
  );
}

export default AdminPermissions;

const ManageBtn = styled.button`
  width: fit-content;
  height: fit-content;
  padding: 7px 23px;
  border-radius: 5px;
  background: var(--admin-blue-default-blue, #0079e0);
  color: white;
  border: #0079e0;
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: center;
`;

